import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {VisualSizesEnum} from '../../styles/commonStyles';
import {fontSizes, fontWeights} from '../../styles/fontStyles';
import {PaletteColorsEnum} from '../../styles/paletteModel';
import {makeSizeConstants} from '../../styles/styleHelpers';
import {ChromelessButton} from '../buttons/chromelessButton';

/*
 * Props.
 */

export interface StepHeaderProps {
  title: ReactNode;
  stepNumber: number;
  isDisabled?: boolean;
  isSelected?: boolean;
  description?: ReactNode;
  size?: VisualSizesEnum;
  palette?: PaletteColorsEnum;
  onClick?: () => void;
}

/*
 * Style.
 */

const StyledStepHeaderButton = styled(ChromelessButton)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface StyledTitleDivProps {
  $isDisabled: boolean;
  $size: VisualSizesEnum;
}

const fontSizeConstants = makeSizeConstants(fontSizes.medium, fontSizes.veryLarge);
const lineHeightConstants = makeSizeConstants('16px', '24px');

const StyledTitleDiv = styled.div<StyledTitleDivProps>`
  font-size: ${(p) => fontSizeConstants[p.$size]};
  line-height: ${(p) => lineHeightConstants[p.$size]};
  font-weight: ${fontWeights.semibold};
  letter-spacing: -0.5px;
  color: ${(p) => (p.$isDisabled ? p.theme.greys.shade60 : p.theme.greys.shade90)};
`;

interface StyledNumberDivProps {
  $isDisabled: boolean;
  $isCurrent: boolean;
  $palette: PaletteColorsEnum;
}

const StyledNumberDiv = styled.div<StyledNumberDivProps>`
  height: 16px;
  width: 16px;
  font-size: 9px;
  line-height: 12px;
  font-weight: ${fontWeights.black};
  border-radius: 50%;
  display: flex;
  justify-content: space-around;
  align-items: center;
  ${(p) => addColorStyles(p)}
`;

function addColorStyles(props: StyledNumberDivProps) {
  if (props.$isDisabled) {
    return css`
      border: 1.5px solid ${(p) => p.theme.greys.shade60};
      background-color: ${(p) => p.theme.foreground};
      color: ${(p) => p.theme.greys.shade60};
    `;
  }

  if (props.$isCurrent) {
    return css`
      border: 1.5px solid ${(p) => p.theme.palette[props.$palette].shade40};
      background-color: ${(p) => p.theme.palette[props.$palette].shade40};
      color: ${(p) => p.theme.greys.white};
    `;
  }

  return css`
    border: 1px solid ${(p) => p.theme.palette[props.$palette].shade40};
    background-color: transparent;
    color: ${(p) => p.theme.palette[props.$palette].shade40};
  `;
}

const StyledDescriptionDiv = styled.div`
  margin-top: 8px;
  margin-bottom: 16px;
  font-size: ${fontSizes.medium};
  line-height: 22px;
  letter-spacing: -0.25px;
  color: ${(p) => p.theme.greys.shade80};
  max-width: 600px;
`;

/*
 * Component.
 */

export const StepHeader: FC<StepHeaderProps> = ({
  title,
  stepNumber,
  isDisabled,
  description,
  isSelected,
  size,
  palette = PaletteColorsEnum.BLUE,
  onClick,
}) => (
  <>
    <StyledStepHeaderButton onClick={onClick}>
      <StyledNumberDiv $isCurrent={Boolean(isSelected)} $isDisabled={Boolean(isDisabled)} $palette={palette}>
        {stepNumber}
      </StyledNumberDiv>
      <StyledTitleDiv $size={size || VisualSizesEnum.MEDIUM} $isDisabled={Boolean(isDisabled)}>
        {title}
      </StyledTitleDiv>
    </StyledStepHeaderButton>
    {!isDisabled && description && <StyledDescriptionDiv>{description}</StyledDescriptionDiv>}
  </>
);
